// Build-time environment config. Every endpoint the frontend talks to comes from here.
//
// Values come from config/environments/<env>/frontend.env (loaded by vite.config.ts, which
// also fails the build when one is missing). There are deliberately no fallbacks: a fallback
// URL silently points a staging build at prod (or, historically, at the dead simcheck-dev
// project). A missing value must break the build, never route traffic somewhere else.

export const required = (key: string): string => {
  const value = import.meta.env[key as keyof ImportMetaEnv] as string | undefined;
  if (!value) {
    throw new Error(
      `Missing build-time env ${key}. It comes from config/environments/<env>/frontend.env ` +
        '(see vite.config.ts); run vite from inside apps/frontend-new/.'
    );
  }
  return value;
};

export type SimcheckEnv = 'prod' | 'staging';

const simcheckEnv = required('VITE_SIMCHECK_ENV');
if (simcheckEnv !== 'prod' && simcheckEnv !== 'staging') {
  throw new Error(`VITE_SIMCHECK_ENV must be 'prod' or 'staging', got '${simcheckEnv}'.`);
}
export const SIMCHECK_ENV: SimcheckEnv = simcheckEnv;
export const IS_STAGING = SIMCHECK_ENV === 'staging';

/** Functions v2 `api` (REST). Routes are at the root, e.g. `${API_URL}/organizations/...`. */
export const API_URL = required('VITE_API_URL');
/** Functions base (`https://us-central1-<project>.cloudfunctions.net`); callers append `/api`, `/aiChatStream`, ... */
export const FUNCTIONS_URL = required('VITE_FUNCTIONS_URL');
/** `executeFlow` function URL. */
export const RUNTIME_API_URL = required('VITE_RUNTIME_API_URL');
/** central-server REST base, including the trailing `/api`. */
export const CENTRAL_API_URL = required('VITE_CENTRAL_API_URL');
/** `tlamProxy` function URL. */
export const TLAM_PROXY_URL = required('VITE_TLAM_PROXY_URL');
/** MCP server (Streamable HTTP) base. */
export const MCP_SERVER_URL = required('VITE_MCP_SERVER_URL');
/** API base used by the TLAM phone browser's WebRTC signalling. */
export const API_BASE_URL = required('VITE_API_BASE_URL');

/** central-server WebSocket origin: CENTRAL_API_URL without the trailing `/api`, https→wss. */
export const CENTRAL_WS_URL = CENTRAL_API_URL.replace(/\/api\/?$/, '')
  .replace(/^https:\/\//, 'wss://')
  .replace(/^http:\/\//, 'ws://');
